// EVA-6902 Unit 2 — replaces `appSlice.visSettings` (Redux, global).
//
// This is the one intentional exception to the "scoped Zustand store per
// widget instance" pattern used elsewhere in this migration: the XDA
// widget's radius-panel settings and every independently-mounted 3D-viewer
// widget on the dashboard must share the exact same dimension values, so a
// single global `create()` store (not the `createStore()` factory + Context
// pattern) is correct here — mirrors `src/stores/map/mapStore.ts` /
// `src/stores/workspace/workspaceStore.ts`.
//
// Side-effect policy: actions are synchronous domain transitions only — the
// debounce that gates writes into this store lives in the caller
// (XdaRadiusOptions.tsx), not here.
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

// ── State ────────────────────────────────────────────────────────────────────
export interface VisDimensionsState {
  readonly leftWidth: number;
  readonly rightWidth: number;
  readonly upHeight: number;
  readonly downHeight: number;
}

// ── Actions ──────────────────────────────────────────────────────────────────
export interface VisDimensionsActions {
  readonly setDimensions: (next: Partial<VisDimensionsState>) => void;
  readonly reset: () => void;
}

export type VisDimensionsStore = VisDimensionsState & VisDimensionsActions;

// Same 4 fields, same defaults as `appSlice.ts`'s `visSettings` today.
export const initialState: VisDimensionsState = {
  leftWidth: 400,
  rightWidth: 400,
  upHeight: 50,
  downHeight: 50
};

export const useVisDimensionsStore = create<VisDimensionsStore>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    setDimensions: (next): void => {
      set(next);
    },

    reset: (): void => {
      set(initialState);
    }
  }))
);
